// scene/glyphs.js — one instanced quad layer per data layer.
//
// One draw call per layer: a unit quad in an InstancedBufferGeometry, billboarded in the vertex
// shader, tinted per instance, sampling the runtime glyph atlas in the fragment shader. Sizes are
// in CSS pixels and clamped to 4..14 so a glyph is the same size at any zoom; the size encodes the
// class, never the true size (docs/design-language.md).
//
// Picking projects the live instance positions to NDC on the CPU and takes the nearest within
// 24 px. Raycasting tens of thousands of instances is the thing this exists to avoid.
//
// A record whose cls is 'sample' also draws a dashed halo ring, so bundled demonstration data can
// never be mistaken for a live position (the module contract rule 5). The ring is drawn by the
// same instance as the dot but at its own opacity (iRing), because the dot yields to a 3D model
// and the ring must not: a model built from bundled coordinates is no more live than a dot is.

import * as THREE from '../../vendor/three.module.min.js';
import * as propagateMod from '../propagate/index.js';
import * as stageMod from './stage.js';
import { earthShadowLit, sunAndEarthScene, orbitsEarth, standsOn, groundLit, worldCentreScene } from './shadow.js';
import { GLSL_AIR } from '../sky/skymath.js';
import { dotOpacity } from './onemark.js';
import {
  getGlyphAtlas,
  glyphCell,
  glyphFor,
  cellUV,
  ATLAS_GRID,
  CELL_PX,
  HALO_BIAS,
  CLASS_COLOURS,
  PALETTE,
} from './glyphatlas.js';

// Size in CSS px by class, before the 4..14 clamp. Class, not true size.
export const GLYPH_SIZE_PX = {
  station: 13,
  satellite: 8,
  debris: 8,
  rocket: 9,
  probe: 9,
  telescope: 9,
  asteroid: 10,
  comet: 10,
  site: 9,
  world: 12,
  // A mark for a planet nobody can see, drawn at the star it goes round: an annotation on the star
  // field, so smaller than the brightest star (stars3d.js tops out at 6 px). With no row it took
  // the satellite's 8 px, and on the stellar rung 150 green marks outdrew the stars they belong to
  // -- one sat on top of Sirius from a light-year out (measured 2026-09-22).
  exoplanet: 5,
  // Shapes that are not classes (glyphatlas.js glyphFor). A crew vehicle is the thing people look
  // for beside a station, so it is a little larger than the satellites it is filed among; a
  // constellation's dot is the size the satellite disc was before satellites had wings.
  crewed: 11,
  dot: 7,
  // The deep-sky marks (spec 0075 task 4): a little under a satellite's 8 px, which they took until
  // then, so a nebula's mark does not outdraw the stars it sits among.
  dso: 7,
};

const DEBRIS_SIZE = 0.6; // 60 % size
const DEBRIS_OPACITY = 0.5; // half opacity
const RENDER_ORDER_DEBRIS = 5; // under everything else that is drawn
const RENDER_ORDER_GLYPH = 10;
const PAD = 1.7; // the quad is 1.7x the glyph, leaving room for the sample halo
const PICK_PX = 24; // the contract's forgiveness rule; a caller passes the pointer's own (pickrank.js REACH)
const PICK_MIN_OPACITY = 0.08; // a dot fainter than this is not drawn to the eye, so it is not tapped
const DEBRIS_PICK_PENALTY = 1.6; // a satellite beats a speck at the same distance
/** How much of a dot is left in the Earth's shadow, seen from space. Ours, illustrative. */
export const SHADOW_OPACITY = 0.6;
const PROVISIONAL_OPACITY = 0.7; // not yet in the public catalogue (spec 0026 req 15)

const VERT = /* glsl */ `
attribute vec3 iOffset;
attribute vec3 iColour;
attribute float iSize;
attribute float iOpacity;
attribute float iLit;      // 1 in sunlight, 0 in Earth's shadow (scene/shadow.js)
attribute float iCell;
attribute float iRing;     // the sample halo's own opacity: what iOpacity was before it yielded
#ifdef GLYPH_AXIS
attribute vec3 iAxis;      // a galaxy's major axis, as an offset from iOffset in scene units; zero: not turned
#endif

uniform float uPxScale;   // world units per CSS pixel, per unit of view depth
uniform float uGrid;
uniform float uPad;
uniform float uSky;       // 1 while the camera stands on the ground (sky/skyview.js), else 0
uniform float uSkyScale;  // how big this layer's marks are from the ground, of their size in space
uniform vec3 uSkyUp;      // the observer's zenith, in view space

varying vec2 vUv;
varying vec3 vColour;
varying float vOpacity;
varying float vHalo;
varying float vLit;
varying float vRing;
varying vec2 vCell0;

#include <common>
#include <logdepthbuf_pars_vertex>
${GLSL_AIR}

void main() {
  vUv = uv;
  vColour = iColour;
  vOpacity = iOpacity;
  vLit = iLit;
  vRing = iRing;

  float cells = uGrid * uGrid;
  float cell = iCell;
  vHalo = step( cells - 0.5, cell );      // cell + 16 means "sample: draw the halo"
  cell -= vHalo * cells;
  float col = mod( cell, uGrid );
  float row = floor( cell / uGrid );
  vCell0 = vec2( col / uGrid, 1.0 - ( row + 1.0 ) / uGrid );

  vec4 mv = modelViewMatrix * vec4( iOffset, 1.0 );
  // FROM THE GROUND a satellite is in the same air as the stars (internal #393): lifted by
  // refraction, half a degree on the horizon, and dimmed by the air it is seen through. The dot is
  // a mark, not a magnitude, so the air takes it down to a third and no further; and one the Earth's
  // shadow has taken is a ghost of itself, because in the sky that is when it goes out.
  if ( uSky > 0.5 ) {
    float dist = length( mv.xyz );
    vec3 d = mv.xyz / max( dist, 1e-9 );
    float sinAlt = clamp( dot( d, uSkyUp ), -1.0, 1.0 );
    float altDeg = degrees( asin( sinAlt ) );
    float lifted = radians( altDeg + airRefractionDeg( altDeg ) );
    vec3 level = d - sinAlt * uSkyUp;
    float ll = length( level );
    if ( ll > 1e-6 ) mv.xyz = ( level / ll * cos( lifted ) + uSkyUp * sin( lifted ) ) * dist;
    float air = pow( 10.0, -0.4 * 0.2 * ( airMass( sin( lifted ) ) - 1.0 ) );
    float seen = max( 0.33, air ) * mix( 0.16, 1.0, iLit );
    vOpacity *= seen;
    vRing *= seen;
  }
  float px = clamp( iSize, 4.0, 14.0 ) * uPad * mix( 1.0, uSkyScale, uSky );
  float s = px * uPxScale * max( -mv.z, 1e-6 );
  vec2 corner = position.xy;
#ifdef GLYPH_AXIS
  // A galaxy's ellipse lies along its major axis as the camera sees it (spec 0075 task 4): the axis's
  // far end projected like any point, its direction on the screen, the quad turned to it.
  if ( dot( iAxis, iAxis ) > 0.0 ) {
    vec4 tip = modelViewMatrix * vec4( iOffset + iAxis, 1.0 );
    vec2 d = tip.xy / max( -tip.z, 1e-9 ) - mv.xy / max( -mv.z, 1e-9 );
    float l = length( d );
    if ( l > 1e-12 ) { d /= l; corner = vec2( corner.x * d.x - corner.y * d.y, corner.x * d.y + corner.y * d.x ); }
  }
#endif
  mv.xy += corner * s;
  gl_Position = projectionMatrix * mv;

  #include <logdepthbuf_vertex>
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uAtlas;
uniform vec3 uOutline;
uniform float uGrid;
uniform float uInset;
uniform float uSky;
uniform float uShadeFade;

varying vec2 vUv;
varying vec3 vColour;
varying float vOpacity;
varying float vHalo;
varying float vLit;
varying float vRing;
varying vec2 vCell0;

#include <common>
#include <logdepthbuf_pars_fragment>

void main() {
  #include <logdepthbuf_fragment>

  vec2 q = clamp( vUv, vec2( uInset ), vec2( 1.0 - uInset ) );
  vec4 t = texture2D( uAtlas, vCell0 + q / uGrid );

  // red channel 0 = the 1.5 px dark keyline, 1 = the class colour
  // In Earth's shadow the dot goes to a dim version of its colour: still there, still its class,
  // visibly not catching the Sun (spec 0026 req 12). The keyline is untouched so it stays readable.
  vec3 rgb = mix( uOutline, mix( vColour * 0.38, vColour, vLit ), t.r );
  // ...and it FADES as well as dims (issue #394): the dim colour alone left a shadowed dot as
  // solid as a sunlit one over the dark side of the globe. From the ground the vertex shader has
  // already taken it down much further (uSky), and this is not applied twice.
  float a = t.a * vOpacity * mix( mix( uShadeFade, 1.0, vLit ), 1.0, uSky );

  if ( vHalo > 0.5 ) {
    vec2 d = vUv - 0.5;
    float r = length( d );
    float ring = smoothstep( 0.395, 0.415, r ) * ( 1.0 - smoothstep( 0.450, 0.470, r ) );
    float ang = atan( d.y, d.x ) * 0.15915494 + 0.5;
    float dash = step( 0.5, fract( ang * 12.0 ) );
    // vRing, NOT vOpacity: a dot that has yielded to its model still came from bundled data, and
    // this ring is the only thing on screen that says so. It is drawn at the record's own opacity.
    float h = ring * dash * 0.85 * vRing;
    rgb = mix( rgb, vColour, step( a, h ) );
    a = max( a, h );
  }

  if ( a < 0.004 ) discard;
  gl_FragColor = vec4( rgb, a );

  #include <colorspace_fragment>
}
`;

// The north celestial pole in sun-inertial (ecliptic J2000) axes.
const POLE_ECL = [0, Math.sin(23.4392911 * Math.PI / 180), Math.cos(23.4392911 * Math.PI / 180)];
/** A major axis on the sky at `pos`, `paDeg` east of north (scene/dsoglow.js majorAxisDir, kept here so the light embed does not fetch the glows). */
function axisOnSky(pos, paDeg) {
  const l = Math.hypot(pos.x, pos.y, pos.z) || 1;
  const u = [pos.x / l, pos.y / l, pos.z / l];
  let e = [POLE_ECL[1] * u[2] - POLE_ECL[2] * u[1], POLE_ECL[2] * u[0] - POLE_ECL[0] * u[2], POLE_ECL[0] * u[1] - POLE_ECL[1] * u[0]];
  const el = Math.hypot(e[0], e[1], e[2]) || 1;
  e = [e[0] / el, e[1] / el, e[2] / el];
  const n = [u[1] * e[2] - u[2] * e[1], u[2] * e[0] - u[0] * e[2], u[0] * e[1] - u[1] * e[0]];
  const c = Math.cos(paDeg * Math.PI / 180), k = Math.sin(paDeg * Math.PI / 180);
  return { x: n[0] * c + e[0] * k, y: n[1] * c + e[1] * k, z: n[2] * c + e[2] * k };
}

function viewportCss() {
  if (typeof window === 'undefined') return { w: 1280, h: 720 };
  return { w: window.innerWidth || 1280, h: window.innerHeight || 720 };
}

function colourOf(record, layer) {
  return (
    (record.meta && record.meta.colour) ||
    record.colour ||
    layer.colour ||
    CLASS_COLOURS[record.klass] ||
    PALETTE.text
  );
}

function sizeOf(record, layer) {
  const shape = glyphFor(record, layer);
  const base = record.sizePx || layer.sizePx || GLYPH_SIZE_PX[shape] || GLYPH_SIZE_PX[record.klass] || GLYPH_SIZE_PX.satellite;
  return record.klass === 'debris' ? base * DEBRIS_SIZE : base;
}

/**
 * @param {THREE.Scene} scene
 * @param {{id?:string, klass?:string, colour?:string, glyph?:string, budget?:number}} layer
 * @returns {{setRecords, update, pick, dispose, setVisible, setSelected, setViewport,
 *            setPositionSource, mesh, count():number}}
 */
/**
 * FROM THE GROUND THE STARS COME FIRST (public #271). A deep-sky object's mark says where a thing
 * is that the eye mostly cannot see; in the ground sky those marks were 8 px discs among stars
 * whose cores are 3 to 7 px, and the sky read as a chart of marks (seen 2026-10-08 over Cairo:
 * Orion behind eleven purple dots). There they are drawn at this share of their size. A satellite's
 * dot is a thing the eye does see, and keeps its size.
 */
export const SKY_MARK_SCALE = { 'deep-sky': 0.5, exotics: 0.5 };

export function createGlyphLayer(scene, layer = {}) {
  const propagate = typeof propagateMod.propagate === 'function' ? propagateMod.propagate : null;
  const stage = stageMod.stage || null;
  // stage.toSceneInto() is toScene() with an out vector; this loop is the reason it exists.
  // tMs is passed through rather than left to stage.tMs, so a frame conversion is done at the
  // instant the position was propagated for even if the caller has not called stage.setTime()
  // first. heroes.js already does this; the two now agree.
  const toSceneInto =
    stage && typeof stage.toSceneInto === 'function'
      ? (p, frame, out, tMs) => stage.toSceneInto(p, frame, out, tMs)
      : stage && typeof stage.toScene === 'function'
        ? (p, frame, out, tMs) => stage.toScene(p, frame, tMs)
        : null;
  let warnedNoPropagator = false;
  /** id -> 0..1, how much of that record's 3D model is on screen; set by main.js from heroes. */
  let modelOpacityOf = null;

  const atlas = getGlyphAtlas();
  const outline = new THREE.Color(PALETTE.space);

  const material = new THREE.ShaderMaterial({
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms: {
      uAtlas: { value: atlas },
      uOutline: { value: outline },
      uGrid: { value: ATLAS_GRID },
      uPad: { value: PAD },
      uInset: { value: 0.5 / CELL_PX },
      uPxScale: { value: 2 / (1.5 * 720) },
      uSky: { value: 0 },
      uSkyScale: { value: SKY_MARK_SCALE[layer.id] || 1 },
      uShadeFade: { value: SHADOW_OPACITY },
      uSkyUp: { value: new THREE.Vector3(0, 1, 0) },
    },
    transparent: true,
    depthTest: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    toneMapped: false, // the class colours are palette tokens, not lit surfaces
  });
  // The deep-sky layer turns its galaxies' marks to their position angles (spec 0075 task 4).
  const axisLayer = layer.klass === 'dso' || layer.id === 'deep-sky';
  if (axisLayer) material.defines = { GLYPH_AXIS: '' };

  // A unit quad, centred. uv (0,0) is the lower left, matching cellUV()'s flipY = true origin.
  const QUAD_POS = new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]);
  const QUAD_UV = new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]);
  const QUAD_IDX = [0, 1, 2, 0, 2, 3];

  let geometry = null;
  let mesh = null;
  let capacity = 0;

  // per-record, in record order
  let records = [];
  let recColour = new Float32Array(0);
  let recSize = new Float32Array(0);
  let recOpacity = new Float32Array(0);
  let recCell = new Float32Array(0);

  // per-live-instance, rewritten every update
  let live = []; // live index -> record
  let livePos = new Float32Array(0); // scene units, xyz per live instance
  let attrOffset = null;
  let attrColour = null;
  let attrLit = null;
  // A colour key (spec 0026 req 11): a function of the record, or null for the class colours.
  let colourFn = null;
  let filterFn = null; // 0077 W3-e: see setFilter
  let currentRecords = [];
  let attrSize = null;
  let attrOpacity = null;
  let attrRing = null;
  let attrCell = null;
  let attrAxis = null;     // the deep-sky layer only (GLYPH_AXIS)
  let recAxis = new Float32Array(0); // per record: the major axis as a unit vector, sun-inertial; 0 = round
  const _tip = { x: 0, y: 0, z: 0 };
  const _axis = new THREE.Vector3();

  let selectedId = null;
  // Where the selection sits in the live arrays after the last update(), or -1: updateSelected().
  let selectedAt = -1;
  let selectedRec = null;
  const uploads = { ticks: 0, bytes: 0, all: 0 };
  let lastCamera = null;
  let viewport = viewportCss();
  let viewportLocked = false; // true once setViewport() is called by hand
  let positionSource = null;
  // A big SGP4 catalogue propagated in a worker (propagate/pool.js), or null: see setPool().
  let pool = null;
  const pooled = { x: 0, y: 0, z: 0, frame: 'earth-inertial' };
  const ephemerisOf = propagateMod.EPHEMERIS_OF || null;
  const emberColour = new THREE.Color(PALETTE.ember);
  const scratch = { x: 0, y: 0, z: 0 };
  const _scene = new THREE.Vector3();

  const isDebrisLayer = layer.klass === 'debris' || layer.glyph === 'debris';

  function allocate(n) {
    if (geometry) geometry.dispose();
    capacity = Math.max(16, n);
    geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex(QUAD_IDX);
    geometry.setAttribute('position', new THREE.BufferAttribute(QUAD_POS.slice(), 3));
    geometry.setAttribute('uv', new THREE.BufferAttribute(QUAD_UV.slice(), 2));

    attrOffset = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    attrColour = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    attrSize = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    attrOpacity = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    attrCell = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    attrLit = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
    attrRing = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    for (const a of [attrOffset, attrColour, attrSize, attrOpacity, attrCell, attrLit, attrRing]) {
      a.setUsage(THREE.DynamicDrawUsage);
    }
    geometry.setAttribute('iOffset', attrOffset);
    geometry.setAttribute('iColour', attrColour);
    geometry.setAttribute('iSize', attrSize);
    geometry.setAttribute('iOpacity', attrOpacity);
    geometry.setAttribute('iCell', attrCell);
    geometry.setAttribute('iLit', attrLit);
    geometry.setAttribute('iRing', attrRing);
    if (axisLayer) {
      attrAxis = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
      attrAxis.setUsage(THREE.DynamicDrawUsage);
      geometry.setAttribute('iAxis', attrAxis);
    }
    geometry.instanceCount = 0;
    // The quad is expanded in view space, so three's bounds are meaningless here.
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), Infinity);

    livePos = attrOffset.array;
    selectedAt = -1;
    selectedRec = null;

    if (mesh) {
      mesh.geometry = geometry;
    } else {
      mesh = new THREE.Mesh(geometry, material);
      // three hands onBeforeRender the renderer and the camera actually being drawn with, which
      // is the only reliable source for the canvas's CSS size and the live projection. Without it
      // the pixel sizes would be guesses about the window rather than measurements of the canvas.
      mesh.onBeforeRender = (r, _scene2, cam) => {
        const el = r && r.domElement;
        if (!viewportLocked && el && el.clientHeight > 0) {
          viewport.w = el.clientWidth;
          viewport.h = el.clientHeight;
        }
        if (cam && cam.isCamera) {
          lastCamera = cam;
          material.uniforms.uPxScale.value =
            2 / (cam.projectionMatrix.elements[5] * Math.max(1, viewport.h));
        }
      };
      mesh.frustumCulled = false;
      mesh.renderOrder = isDebrisLayer ? RENDER_ORDER_DEBRIS : RENDER_ORDER_GLYPH;
      mesh.name = `glyphs:${layer.id || layer.klass || 'layer'}`;
      mesh.userData.layer = layer;
      scene.add(mesh);
    }
  }

  function setRecords(next) {
    records = Array.isArray(next) ? next : [];
    // data/layers.js writes `budget: {maxItems, rank}`; the module contract's LAYERS shape says a plain
    // number. Accept both -- comparing a length against the object silently produced NaN, so this
    // guard never fired at all. (loadLayer applies the same cap, so this is the second line of
    // defence for a caller that hands over an unfiltered array.)
    const maxItems =
      typeof layer.budget === 'number'
        ? layer.budget
        : layer.budget && Number.isFinite(layer.budget.maxItems)
          ? layer.budget.maxItems
          : Infinity;
    if (records.length > maxItems) records = records.slice(0, maxItems);
    const n = records.length;
    if (n > capacity) allocate(Math.ceil(n * 1.25));
    else if (!geometry) allocate(n);

    currentRecords = records;
    recColour = new Float32Array(n * 3);
    recSize = new Float32Array(n);
    recOpacity = new Float32Array(n);
    recCell = new Float32Array(n);
    recAxis = new Float32Array(axisLayer ? n * 3 : 0);

    const c = new THREE.Color();
    for (let i = 0; i < n; i++) {
      const r = records[i];
      c.set((colourFn && colourFn(r)) || colourOf(r, layer));
      recColour[i * 3] = c.r;
      recColour[i * 3 + 1] = c.g;
      recColour[i * 3 + 2] = c.b;
      recSize[i] = sizeOf(r, layer);
      // A provisional object (spec 0026 req 15) is drawn at 70%: there, but visibly not yet a catalogued thing.
      recOpacity[i] = r.klass === 'debris' ? DEBRIS_OPACITY : r.meta && r.meta.provisional ? PROVISIONAL_OPACITY : 1;
      recCell[i] = glyphCell(glyphFor(r, layer)) + (r.cls === 'sample' ? HALO_BIAS : 0);
      // A galaxy measured clearly longer than wide, with its angle: its mark is turned to it.
      const md = r.meta || {};
      if (axisLayer && md.kind === 'galaxy' && r.pos && Number.isFinite(md.posAngDeg) && md.majAxArcmin > 0 && md.minAxArcmin > 0 && md.minAxArcmin / md.majAxArcmin < 0.9) {
        const dir = axisOnSky(r.pos, md.posAngDeg);
        recAxis[i * 3] = dir.x; recAxis[i * 3 + 1] = dir.y; recAxis[i * 3 + 2] = dir.z;
      }
    }
    live = [];
    if (geometry) geometry.instanceCount = 0;
    if (pool) pool.setRecords(records);
  }

  function positionOf(record, tMs) {
    if (positionSource) return positionSource(record, tMs);
    if (!propagate) {
      if (!warnedNoPropagator) {
        warnedNoPropagator = true;
        // Degrade, never go dark: draw nothing rather than throw, and say so once.
        console.warn('glyphs: no propagate() available; layer', layer.id, 'draws nothing');
      }
      return null;
    }
    return propagate(record, tMs);
  }

  function update(tMs, camera) {
    const shadowRef = layer.frame === 'earth-inertial' || layer.frame === 'earth-fixed' || layer.frame === undefined ? sunAndEarthScene(tMs) : null;
    if (!geometry) return;
    if (camera) {
      lastCamera = camera;
      const f = camera.projectionMatrix.elements[5]; // 1 / tan(fovY / 2)
      // ndc.y = f * y_view / -z_view, and ndc spans the viewport height, so one CSS pixel is
      // 2 * -z_view / ( f * heightCss ) world units.
      material.uniforms.uPxScale.value = 2 / (f * Math.max(1, viewport.h));
    }
    if (!mesh.visible) return;

    let k = 0;
    // WRITE ONLY WHAT CHANGED, AND SEND ONLY WHAT WAS WRITTEN (internal #519, 2026-10-09). This loop
    // used to mark all seven instanced attributes for upload on every tick: 44 bytes an instance,
    // 734 kB a tick with the whole catalogue on, ten ticks a second at the live clock and sixty at
    // a fast one. But a dot's colour, size, opacity, glyph and halo change when the records, the
    // selection, a recolour or a model's fade change, not with time; and its sunlit flag only as
    // it crosses the Earth's shadow. So each value is compared with the one already in the array,
    // which IS what the GPU holds (an attribute is uploaded whole whenever it is marked), and an
    // attribute is marked only when one of its values differed. There is no list of reasons to
    // keep right: a selection, a filter, a record that drops out and shifts every index after it,
    // a new array from setRecords() all change a value, and a changed value marks its attribute.
    // The comparisons are in float32 (Math.fround), because that is what the arrays hold.
    // tests/test_glyph_uploads.mjs keeps a copy of each array as a GPU would and fails if one ever
    // differs from its array without having been marked.
    let dOffset = false;
    let dColour = false;
    let dSize = false;
    let dOpacity = false;
    let dCell = false;
    let dLit = false;
    let dRing = false;
    let dAxis = false;
    const aColour = attrColour.array;
    const aSize = attrSize.array;
    const aOpacity = attrOpacity.array;
    const aCell = attrCell.array;
    const aLit = attrLit.array;
    const aRing = attrRing.array;
    const emberR = Math.fround(emberColour.r);
    const emberG = Math.fround(emberColour.g);
    const emberB = Math.fround(emberColour.b);
    live.length = 0;
    selectedAt = -1;
    selectedRec = null;
    // The worker's answer, when there is one (propagate/pool.js): the same SGP4 numbers, computed
    // off this thread for this tick or the one before. Until the first answer lands, with a
    // position source set by hand, or once the worker has failed, every record is asked here as
    // before. The selection is always asked here, at this instant exactly; so is a craft that has
    // its own path file.
    let workerPos = null;
    let workerMask = null;
    if (pool && pool.ok && !positionSource) {
      pool.request(tMs);
      const answer = pool.latest;
      if (answer && answer.pos.length === records.length * 3) { workerPos = answer.pos; workerMask = pool.mask; }
    }
    const ownPaths = ephemerisOf && ephemerisOf.size ? ephemerisOf : null;
    for (let i = 0; i < records.length; i++) {
      const rec = records[i];
      // 0077 W3-e: begin (internal i#886: the confidence filter). A record the filter leaves out is not drawn and not tappable, like
      // one with no position; the selection stays whatever the filter says, so a card never loses its own dot.
      if (filterFn !== null && rec.id !== selectedId && !filterFn(rec)) continue;
      // 0077 W3-e: end
      let p = null;
      if (workerPos && workerMask[i] === 1 && rec.id !== selectedId && !(ownPaths && ownPaths.has(rec.id))) {
        const x = workerPos[i * 3];
        if (x !== x) continue; // NaN: SGP4 had no answer for it
        pooled.x = x;
        pooled.y = workerPos[i * 3 + 1];
        pooled.z = workerPos[i * 3 + 2];
        pooled.frame = rec.frame || 'earth-inertial';
        p = pooled;
      } else {
        try {
          p = positionOf(rec, tMs);
        } catch {
          p = null;
        }
      }
      if (!p) continue;
      let v = p;
      if (toSceneInto) {
        scratch.x = p.x;
        scratch.y = p.y;
        scratch.z = p.z;
        v = toSceneInto(scratch, p.frame || rec.frame, _scene, tMs);
        if (!v) continue;
      }
      const o = k * 3;
      const px = Math.fround(v.x);
      const py = Math.fround(v.y);
      const pz = Math.fround(v.z);
      if (livePos[o] !== px || livePos[o + 1] !== py || livePos[o + 2] !== pz) {
        livePos[o] = px;
        livePos[o + 1] = py;
        livePos[o + 2] = pz;
        dOffset = true;
      }
      if (attrAxis) {
        // The axis's far end, 1/1000 of the way out, through the same conversion as the centre.
        let ax = 0, ay = 0, az = 0;
        const ux = recAxis[i * 3], uy = recAxis[i * 3 + 1], uz = recAxis[i * 3 + 2];
        if ((ux || uy || uz) && toSceneInto) {
          const k3 = Math.hypot(p.x, p.y, p.z) * 1e-3;
          _tip.x = p.x + ux * k3; _tip.y = p.y + uy * k3; _tip.z = p.z + uz * k3;
          const w = toSceneInto(_tip, p.frame || rec.frame, _axis, tMs);
          if (w) { ax = Math.fround(w.x - px); ay = Math.fround(w.y - py); az = Math.fround(w.z - pz); }
        }
        const aAxis = attrAxis.array;
        if (aAxis[o] !== ax || aAxis[o + 1] !== ay || aAxis[o + 2] !== az) { aAxis[o] = ax; aAxis[o + 1] = ay; aAxis[o + 2] = az; dAxis = true; }
      }
      const selected = selectedId !== null && rec.id === selectedId;
      if (selected) { selectedAt = k; selectedRec = rec; }
      const cr = selected ? emberR : recColour[i * 3];
      const cg = selected ? emberG : recColour[i * 3 + 1];
      const cb = selected ? emberB : recColour[i * 3 + 2];
      if (aColour[o] !== cr || aColour[o + 1] !== cg || aColour[o + 2] !== cb) {
        aColour[o] = cr;
        aColour[o + 1] = cg;
        aColour[o + 2] = cb;
        dColour = true;
      }
      // Sunlit or in Earth's shadow, for the things that go round the Earth; everything else is lit.
      // A lander on another world's night side dims the same way (scene/shadow.js, public #403).
      let lit = 1;
      if (shadowRef) {
        if (orbitsEarth(rec)) lit = earthShadowLit(v, shadowRef.earth, shadowRef.sun, shadowRef.radius);
        else {
          const world = standsOn(rec);
          const centre = world ? worldCentreScene(world, tMs) : null;
          if (centre) lit = groundLit(v, centre, shadowRef.sun);
        }
      }
      lit = Math.fround(lit);
      if (aLit[k] !== lit) { aLit[k] = lit; dLit = true; }
      const size = selected ? Math.fround(recSize[i] * 1.35) : recSize[i];
      if (aSize[k] !== size) { aSize[k] = size; dSize = true; }
      // An object is drawn once. While its model is on screen the dot yields to it -- by the
      // model's own fade, so neither blinks -- and it stays in `live`, so it is still there to
      // tap. No model, a hidden model, or the hero layer off: the dot, as before.
      const yieldTo = modelOpacityOf ? modelOpacityOf(rec.id) : 0;
      const own = selected ? 1 : recOpacity[i];
      const opacity = Math.fround(dotOpacity(own, yieldTo));
      if (aOpacity[k] !== opacity) { aOpacity[k] = opacity; dOpacity = true; }
      // The sample halo does NOT yield: see the note in the fragment shader. It is drawn at the
      // record's own opacity, which is what the dot's was before the model took it.
      if (aRing[k] !== own) { aRing[k] = own; dRing = true; }
      if (aCell[k] !== recCell[i]) { aCell[k] = recCell[i]; dCell = true; }
      live.push(rec);
      k++;
      if (k >= capacity) break;
    }

    geometry.instanceCount = k;
    if (dOffset) attrOffset.needsUpdate = true;
    if (dColour) attrColour.needsUpdate = true;
    if (dSize) attrSize.needsUpdate = true;
    if (dOpacity) attrOpacity.needsUpdate = true;
    if (dCell) attrCell.needsUpdate = true;
    if (dLit) attrLit.needsUpdate = true;
    if (dRing) attrRing.needsUpdate = true;
    if (dAxis) attrAxis.needsUpdate = true;
    uploads.ticks += 1;
    uploads.bytes += 4 * k * ((dOffset ? 3 : 0) + (dColour ? 3 : 0) + (dSize ? 1 : 0) + (dOpacity ? 1 : 0) + (dCell ? 1 : 0) + (dLit ? 1 : 0) + (dRing ? 1 : 0));
    uploads.all += 4 * k * 11;
    if (!material.uniforms.uAtlas.value) {
      const a = getGlyphAtlas();
      if (a) material.uniforms.uAtlas.value = a;
    }
  }

  /**
   * THE SELECTION'S DOT, PLACED AGAIN ON A FRAME BETWEEN TWO TICKS (Ivan, 2026-10-14: "sometimes
   * when an object is selected it is shaking on the screen"). At the live clock main.js calls
   * update() ten times a second, which is enough for a dot nobody is watching. The selection is
   * watched: the camera follows it and the brackets (ui/hud.js) are placed on it EVERY frame, from
   * the same propagator. So between two ticks the camera and the brackets went on and the dot
   * stayed, then jumped: 0.1 s of a low orbit is 760 m, which is 1 px from 700 km and 15 px from
   * 50 km, ten times a second; from the ground through a telescope's field the same sawtooth is
   * the Earth's turn under a geostationary dot. One record, three floats: the rest of the layer
   * keeps its tick. False when this layer does not hold the selection (or holds it undrawn).
   */
  function updateSelected(tMs) {
    if (selectedAt < 0 || !selectedRec || !geometry || !mesh.visible || selectedRec.id !== selectedId) return false;
    if (selectedAt >= geometry.instanceCount || live[selectedAt] !== selectedRec) return false;
    let p = null;
    try { p = positionOf(selectedRec, tMs); } catch { p = null; }
    if (!p) return false;
    let v = p;
    if (toSceneInto) {
      scratch.x = p.x;
      scratch.y = p.y;
      scratch.z = p.z;
      v = toSceneInto(scratch, p.frame || selectedRec.frame, _scene, tMs);
      if (!v) return false;
    }
    const o = selectedAt * 3;
    const px = Math.fround(v.x);
    const py = Math.fround(v.y);
    const pz = Math.fround(v.z);
    if (livePos[o] === px && livePos[o + 1] === py && livePos[o + 2] === pz) return true;
    livePos[o] = px;
    livePos[o + 1] = py;
    livePos[o + 2] = pz;
    attrOffset.needsUpdate = true;
    uploads.bytes += 4 * geometry.instanceCount * 3;
    return true;
  }

  const _m = new THREE.Matrix4();

  /**
   * Every record within the forgiveness rule, nearest first, each with its pixel distance and its
   * penalised score -- for scene/pickrank.js to weigh against other layers and for a long press to
   * list. pick() is the one-winner form of the same scan.
   */
  function pickAll(ndcX, ndcY, limit = 6, reachPx = PICK_PX, camera = null, vp = null) {
    const cam = camera || lastCamera;
    if (!cam || !geometry || !mesh.visible || geometry.instanceCount === 0) return [];
    cam.updateMatrixWorld();
    mesh.updateMatrixWorld();
    _m.multiplyMatrices(cam.projectionMatrix, cam.matrixWorldInverse);
    _m.multiply(mesh.matrixWorld);
    const e = _m.elements;
    // The canvas as it is now (the caller's), not the size it had when this layer last drew.
    const halfW = (vp ? vp.w : viewport.w) * 0.5;
    const halfH = (vp ? vp.h : viewport.h) * 0.5;
    const out = [];
    for (let i = 0; i < geometry.instanceCount; i++) {
      const x = livePos[i * 3];
      const y = livePos[i * 3 + 1];
      const z = livePos[i * 3 + 2];
      const w = e[3] * x + e[7] * y + e[11] * z + e[15];
      if (w <= 0) continue;
      const cx = (e[0] * x + e[4] * y + e[8] * z + e[12]) / w;
      const cy = (e[1] * x + e[5] * y + e[9] * z + e[13]) / w;
      const dx = (cx - ndcX) * halfW;
      const dy = (cy - ndcY) * halfH;
      const px = Math.sqrt(dx * dx + dy * dy);
      if (px > reachPx) continue;
      // Not drawn is not tappable: a record the layer has faded out (its own opacity, not the model's yield).
      if (attrRing.array[i] < PICK_MIN_OPACITY && !(selectedRec && live[i] === selectedRec)) continue;
      const rec = live[i];
      if (!rec) continue;
      out.push({ record: rec, px, score: rec.klass === 'debris' ? px * DEBRIS_PICK_PENALTY : px, sx: (cx + 1) * halfW, sy: (1 - cy) * halfH, depth: w });
    }
    out.sort((p, q) => p.score - q.score);
    return out.slice(0, Math.max(1, limit));
  }

  /**
   * Recolour every record by a key (spec 0026 req 11), or back to the class colours with null. Only
   * the per-record colour table changes; the next update() copies it into the instance buffer,
   * with the selection's ember still winning.
   */
  // 0077 W3-e: begin
  /** fn(record) -> boolean: only the records it says yes to are drawn (ui/confidence.js); null puts them all back. */
  function setFilter(fn) { filterFn = typeof fn === 'function' ? fn : null; }
  // 0077 W3-e: end
  function recolour(fn) {
    colourFn = typeof fn === 'function' ? fn : null;
    if (!recColour) return;
    const c = new THREE.Color();
    for (let i = 0; i < currentRecords.length; i++) {
      const r = currentRecords[i];
      c.set((colourFn && colourFn(r)) || colourOf(r, layer));
      recColour[i * 3] = c.r; recColour[i * 3 + 1] = c.g; recColour[i * 3 + 2] = c.b;
    }
  }

  function pick(ndcX, ndcY) {
    if (!lastCamera || !geometry || !mesh.visible || geometry.instanceCount === 0) return null;
    lastCamera.updateMatrixWorld();
    mesh.updateMatrixWorld();
    _m.multiplyMatrices(lastCamera.projectionMatrix, lastCamera.matrixWorldInverse);
    _m.multiply(mesh.matrixWorld);
    const e = _m.elements;
    const halfW = viewport.w * 0.5;
    const halfH = viewport.h * 0.5;
    let best = null;
    let bestScore = PICK_PX * DEBRIS_PICK_PENALTY;
    for (let i = 0; i < geometry.instanceCount; i++) {
      const x = livePos[i * 3];
      const y = livePos[i * 3 + 1];
      const z = livePos[i * 3 + 2];
      const w = e[3] * x + e[7] * y + e[11] * z + e[15];
      if (w <= 0) continue; // behind the camera
      const cx = (e[0] * x + e[4] * y + e[8] * z + e[12]) / w;
      const cy = (e[1] * x + e[5] * y + e[9] * z + e[13]) / w;
      const dx = (cx - ndcX) * halfW;
      const dy = (cy - ndcY) * halfH;
      const px = Math.sqrt(dx * dx + dy * dy);
      if (px > PICK_PX) continue;
      const rec = live[i];
      const score = rec && rec.klass === 'debris' ? px * DEBRIS_PICK_PENALTY : px;
      if (score < bestScore) {
        bestScore = score;
        best = rec;
      }
    }
    return best;
  }

  allocate(16);

  return {
    setRecords,
    updateSelected,
    /** Where the selection's dot is drawn, as the GPU holds it ([x, y, z], scene units), or null: for tests and probes. */
    selectedOffset() {
      if (selectedAt < 0 || !geometry || selectedAt >= geometry.instanceCount || live[selectedAt] !== selectedRec) return null;
      return [livePos[selectedAt * 3], livePos[selectedAt * 3 + 1], livePos[selectedAt * 3 + 2]];
    },
    /** fn(id) -> 0..1: the dot for that record yields to its model by this much. */
    setModelOpacity(fn) { modelOpacityOf = typeof fn === 'function' ? fn : null; },
    update,
    pick,
    pickAll,
    recolour,
    setFilter, // 0077 W3-e
    setVisible(b) {
      if (mesh) mesh.visible = !!b;
    },
    /**
     * The view from the ground (sky/skyview.js): `up` is the observer's zenith in VIEW space, and
     * the shader then lifts and dims every dot as the air does a star. `null` is the orbital view.
     */
    setSky(up) {
      material.uniforms.uSky.value = up ? 1 : 0;
      if (up) material.uniforms.uSkyUp.value.copy(up);
    },
    dispose() {
      if (pool) pool.dispose();
      pool = null;
      if (mesh) scene.remove(mesh);
      if (geometry) geometry.dispose();
      material.dispose();
      records = [];
      live = [];
      mesh = null;
      geometry = null;
    },
    // --- beyond the contract, all optional; the layer works without any of them ---
    /** Highlight one record in ember (the selection colour, and the only interactive colour). */
    setSelected(id) {
      selectedId = id === undefined ? null : id;
      selectedAt = -1; // found again by the next update()
      selectedRec = null;
    },
    /**
     * Override the CSS pixel size of the canvas. Not normally needed: the layer measures the
     * canvas itself at draw time. Calling this pins the value until the layer is disposed.
     */
    setViewport(w, h) {
      viewport = { w: w || viewport.w, h: h || viewport.h };
      viewportLocked = true;
    },
    /** Replace propagate() as the position source, e.g. a table filled by a worker. */
    setPositionSource(fn) {
      positionSource = typeof fn === 'function' ? fn : null;
    },
    /**
     * Hand the layer's SGP4 to a worker (propagate/pool.js `createPropagationPool()`), or take it
     * back with null. The layer gives the pool its records now and on every setRecords().
     */
    setPool(next) {
      if (pool && pool !== next) pool.dispose();
      pool = next || null;
      if (pool) pool.setRecords(records);
    },
    /**
     * What this layer has marked for upload since it was made: `bytes` of live instances in the
     * attributes it marked, beside `all`, what marking every attribute on every tick would have
     * been (the behaviour before internal #519). For probes and tests; nothing reads it per frame.
     */
    uploadStats() {
      return { ...uploads };
    },
    /** The pool's own account of itself, or null: for probes and `spaceRadar.perf()`. */
    poolStats() {
      return pool ? { ok: pool.ok, ...pool.stats } : null;
    },
    get mesh() {
      return mesh;
    },
    count() {
      return geometry ? geometry.instanceCount : 0;
    },
  };
}

export { cellUV };
